import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FusionLogo from './FusionLogo';
import AppFooter from './AppFooter';
import { FUSION_CONTACT } from '../lib/fusionContact';

// BRIEF-27 — reusable supplier guide / help-article template. Rebuilt from the
// old single-column "wall of text" into a scannable, navigable layout that
// matches the redesigned portal:
//   - dark chrome header + "Back to portal"
//   - title card (green top accent, breadcrumb, H1, intro, read-time)
//   - two-column grid: sticky "On this page" contents nav (scroll-spy) +
//     a stack of elevated section cards
//   - a kit of reusable content blocks (status cards, lifecycle stepper,
//     comparison, info callout, contact footer)
//
// Content-agnostic: an article passes an ordered `sections` array
// ({ id, title, icon, body }) and the layout builds the nav + cards from it.
// Articles not yet migrated can still pass `children` (rendered in one card,
// no nav) so the four pages keep working during the roll-out.

// ---------------------------------------------------------------------------
// Shared inline building blocks (kept from the previous layout for authors)
// ---------------------------------------------------------------------------

export const ArticleH2 = ({ children }) => (
  <h2 className="font-['Metropolis'] text-[13px] font-bold tracking-[0.25em] uppercase text-[#009159] mt-8 mb-3.5 pb-2.5 border-b border-line first:mt-0">
    {children}
  </h2>
);

export const UiLabel = ({ children }) => (
  <em className="not-italic text-ink font-semibold bg-[#92D400]/[0.08] px-1.5 py-px rounded-[3px] text-[12px]">
    {children}
  </em>
);

export const DocName = ({ children }) => (
  <span className="inline-block bg-[#2E8FA1]/10 text-[#2E8FA1] border border-[#2E8FA1]/30 px-1.5 py-px rounded-[3px] text-[11px] font-bold tracking-[0.05em]">
    {children}
  </span>
);

export const StatusChip = ({ tone, children }) => {
  const cfg = {
    outstanding: 'text-[#E0290A] border-[#E0290A] bg-[#E0290A]/[0.08]',
    review:      'text-[#2E8FA1] border-[#2E8FA1] bg-[#2E8FA1]/[0.08]',
    approved:    'text-fusion-forest border-[#92D400] bg-[#92D400]/[0.08]',
    expiring:    'text-[#7A4E10] border-[#E89A2E] bg-[#E89A2E]/[0.08]',
    expired:     'text-[#8E1B06] border-[#8E1B06] bg-[#8E1B06]/[0.08]',
  }[tone] || 'text-[#51534A] border-[#DADCCD] bg-[#51534A]/[0.08]';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-px border rounded-full text-[10px] font-bold tracking-[0.15em] uppercase align-middle ${cfg}`}>
      {children}
    </span>
  );
};

// ---------------------------------------------------------------------------
// §5 — reusable content components (the kit)
// ---------------------------------------------------------------------------

// Status cards — responsive row of tinted tiles: coloured dot + label +
// description. Part D colours only; amber is the sanctioned compliance use.
export const StatusCards = ({ items = [] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map((it, i) => (
      <div key={i} className="rounded-xl border p-4" style={{ background: `${it.color}0F`, borderColor: `${it.color}40` }}>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: it.color }} aria-hidden="true" />
          <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase" style={{ color: it.textColor || it.color }}>{it.label}</span>
        </div>
        <p className="font-['Metropolis'] text-[13px] text-[#4C4E42] mt-1.5 leading-relaxed">{it.description}</p>
      </div>
    ))}
  </div>
);

// Lifecycle stepper — horizontal step flow with coloured nodes + connectors.
// Required grey, Under review teal, Approved deep-green. Exception states
// render as a red-accented note beneath (not a full red fill).
export const LifecycleStepper = ({ steps = [], note }) => (
  <div>
    <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-0">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <div className="flex sm:flex-col sm:items-center sm:text-center gap-3 sm:gap-2 sm:flex-1 min-w-0">
            <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-['Metropolis'] text-[12px] font-extrabold text-white" style={{ background: s.color }}>{i + 1}</span>
            <div className="min-w-0">
              <div className="font-['Metropolis'] text-[13px] font-bold text-ink leading-tight">{s.label}</div>
              {s.description && <div className="font-['Metropolis'] text-[12px] text-[#7C7F6F] mt-0.5 leading-snug">{s.description}</div>}
            </div>
          </div>
          {i < steps.length - 1 && <span aria-hidden="true" className="hidden sm:block flex-shrink-0 h-px w-6 mt-4 bg-[#D4D9C6]" />}
        </React.Fragment>
      ))}
    </div>
    {note && (
      <div className="mt-4 flex items-start gap-2.5 rounded-lg px-4 py-3" style={{ background: 'rgba(224,41,10,0.05)', borderLeft: '3px solid #E0290A' }}>
        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="#E0290A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5" /><path d="M12 16h.01" /></svg>
        <p className="font-['Metropolis'] text-[12px] text-[#4C4E42] leading-relaxed">{note}</p>
      </div>
    )}
  </div>
);

// Comparison — two side-by-side cards for "this vs that" content, each with a
// coloured eyebrow + tag pill.
export const Comparison = ({ left, right }) => {
  const Card = (c) => (
    <div className="flex-1 rounded-xl border border-[#E6EADB] bg-[#F7F9F0] p-5">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase" style={{ color: c.color }}>{c.eyebrow}</span>
        {c.tag && <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.1em] uppercase rounded-full px-2 py-0.5" style={{ background: `${c.color}18`, color: c.color }}>{c.tag}</span>}
      </div>
      <div className="font-['Metropolis'] text-[15px] font-bold text-ink">{c.title}</div>
      <p className="font-['Metropolis'] text-[13px] text-[#4C4E42] mt-1.5 leading-relaxed">{c.body}</p>
    </div>
  );
  return <div className="flex flex-col sm:flex-row gap-3">{Card(left)}{Card(right)}</div>;
};

// Info callout — teal-tinted, lightly elevated clarification box.
export const InfoCallout = ({ tone = 'info', children }) => {
  const cfg = {
    info: { bg: '#2E8FA1', },
    positive: { bg: '#009159', },
    warning: { bg: '#E89A2E', },
    danger: { bg: '#E0290A', },
  }[tone] || { bg: '#2E8FA1' };
  return (
    <div className="rounded-xl p-4 flex gap-3 shadow-[0_1px_2px_rgba(45,48,35,0.04),0_5px_12px_-4px_rgba(45,48,35,0.08)]" style={{ background: `${cfg.bg}0D`, border: `1px solid ${cfg.bg}33` }}>
      <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${cfg.bg}1F` }} aria-hidden="true">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={cfg.bg} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
      </span>
      <div className="font-['Metropolis'] text-[13px] text-[#4C4E42] leading-relaxed self-center">{children}</div>
    </div>
  );
};

// Backward-compatible Callout (old API: tone info/warning/danger/positive).
export const Callout = ({ tone = 'info', children }) => (
  <div className="my-4"><InfoCallout tone={tone === 'positive' ? 'positive' : tone}>{children}</InfoCallout></div>
);

// Contact footer — "Still need help?" card with support email + phone.
export const ContactFooter = ({ text }) => (
  <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6">
    <div className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#009159] mb-2">Still need help?</div>
    {text && <p className="font-['Metropolis'] text-[13px] text-[#4C4E42] leading-relaxed mb-3">{text}</p>}
    <div className="flex flex-wrap gap-x-5 gap-y-2 items-center">
      <a href={`mailto:${FUSION_CONTACT.email}`} className="inline-flex items-center gap-1.5 text-[#2E8FA1] font-semibold text-[13px] hover:text-[#009159] no-underline transition-colors">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
        {FUSION_CONTACT.email}
      </a>
      <a href={`tel:${FUSION_CONTACT.phoneFormatted}`} className="inline-flex items-center gap-1.5 text-[#2E8FA1] font-semibold text-[13px] hover:text-[#009159] no-underline transition-colors">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
        {FUSION_CONTACT.phoneDisplay}
      </a>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export default function HelpArticleLayout({
  breadcrumb,
  title,
  intro,
  readTime,       // e.g. "4 min read"
  sections,       // ordered [{ id, title, icon, body }] — the content model
  footerText,
  children,       // fallback for not-yet-migrated articles
}) {
  const navigate = useNavigate();
  const hasSections = Array.isArray(sections) && sections.length > 0;
  const [active, setActive] = useState(hasSections ? sections[0].id : null);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!hasSections) return undefined;
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter(e => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-15% 0px -75% 0px', threshold: 0 });
    Object.values(sectionRefs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, [hasSections, sections]);

  const jumpTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActive(id); }
  };

  return (
    <div className="min-h-screen bg-canvas">
      {/* Dark chrome header */}
      <div className="bg-surface-sidebar border-b border-white/[0.08]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 h-[76px] sm:h-[90px] flex items-center justify-between gap-6">
          <button type="button" onClick={() => navigate('/portal')} aria-label="Fusion 2K Supply Pilot — home" title="Home" className="flex items-center gap-2.5 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity">
            <FusionLogo icon alt="Fusion" className="h-[38px] w-auto object-contain" />
            <span className="font-['Metropolis'] text-[19px] font-extrabold tracking-[0.14em] text-white leading-none">FUSION</span>
            <span className="hidden sm:inline font-['Metropolis'] text-[12px] font-bold tracking-[0.16em] uppercase text-sb-text border-l border-white/15 pl-3.5">Supply Pilot · Partner Portal</span>
          </button>
          <button type="button" onClick={() => navigate('/portal')} className="font-['Metropolis'] text-xs font-bold tracking-[0.18em] uppercase text-sb-muted hover:text-white transition-colors bg-transparent border-0 cursor-pointer whitespace-nowrap">
            ← Back to portal
          </button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 sm:px-8 pt-8 pb-16">
        {/* Title card */}
        <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift overflow-hidden mb-5">
          <div aria-hidden="true" className="h-1 w-full bg-[#92D400]" />
          <div className="p-6 sm:p-8">
            <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.22em] uppercase text-[#51534A] mb-3 flex items-center gap-2">
              <button type="button" onClick={() => navigate('/portal')} className="text-[#51534A] hover:text-ink transition-colors bg-transparent border-0 p-0 cursor-pointer font-['Metropolis'] font-bold tracking-[0.22em] uppercase">Help &amp; Contact</button>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="text-[#009159]">{breadcrumb}</span>
            </div>
            <h1 className="font-['Metropolis'] text-[26px] sm:text-[30px] font-extrabold text-ink leading-tight tracking-tight">{title}</h1>
            {intro && <p className="font-['Metropolis'] text-[15px] text-[#4C4E42] leading-relaxed mt-3 max-w-[64ch]">{intro}</p>}
            {(readTime || hasSections) && (
              <div className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.08em] uppercase text-[#7C7F6F] mt-4 flex items-center gap-2">
                {readTime && <span>{readTime}</span>}
                {readTime && hasSections && <span aria-hidden="true">·</span>}
                {hasSections && <span>{sections.length} section{sections.length === 1 ? '' : 's'}</span>}
              </div>
            )}
          </div>
        </div>

        {hasSections ? (
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5 lg:gap-8 items-start">
            {/* Contents nav — sticky, scroll-spy */}
            <nav aria-label="On this page" className="hidden lg:block lg:sticky lg:top-6">
              <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#009159] mb-3">On this page</div>
              <ul className="space-y-1">
                {sections.map(s => {
                  const isActive = active === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => jumpTo(s.id)}
                        className={`w-full text-left font-['Metropolis'] text-[13px] leading-snug pl-3 py-1.5 border-l-2 transition-colors ${
                          isActive ? 'border-[#92D400] text-[#009159] font-bold' : 'border-transparent text-[#51534A] hover:text-fusion-mid-grey hover:border-[#D4D9C6]'
                        }`}>
                        {s.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Section cards */}
            <div className="min-w-0 space-y-5">
              {sections.map(s => (
                <section
                  key={s.id}
                  id={s.id}
                  ref={el => { sectionRefs.current[s.id] = el; }}
                  className="scroll-mt-6 bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6 sm:p-7"
                >
                  <div className="flex items-center gap-3 mb-4">
                    {s.icon && (
                      <span className="w-8 h-8 rounded-lg bg-[#009159]/[0.10] flex items-center justify-center flex-shrink-0 text-[#009159]" aria-hidden="true">{s.icon}</span>
                    )}
                    <h2 className="font-['Metropolis'] text-[18px] font-bold text-ink tracking-tight leading-tight">{s.title}</h2>
                  </div>
                  <div className="font-['Metropolis'] text-[14px] text-[#4C4E42] leading-[1.65] max-w-[62ch] space-y-3">{s.body}</div>
                </section>
              ))}
              <ContactFooter text={footerText} />
            </div>
          </div>
        ) : (
          // Fallback — unmigrated articles render children in one elevated card.
          <div className="space-y-5">
            <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6 sm:p-8">
              <div className="font-['Metropolis'] text-[14px] text-[#4C4E42] leading-[1.65] max-w-[64ch]">{children}</div>
            </div>
            <ContactFooter text={footerText} />
          </div>
        )}

        <div className="mt-8"><AppFooter /></div>
      </div>
    </div>
  );
}
